type CurrencyDisplay =
  | { style: 'symbol'; symbol: string }
  | { style: 'code' }
  | { style: 'symbol-and-code'; symbol: string }

/**
 * Customer-facing display per currency (see "Supported currency and format").
 * Symbol-only where the symbol is unambiguous, code-only where the currency
 * has no short symbol, symbol plus code for every market that shares `$`
 * (and KRW). Anything not listed falls back to code-only, which is never
 * ambiguous.
 */
export const CURRENCY_DISPLAY: Readonly<Record<string, CurrencyDisplay>> = {
  EUR: { style: 'symbol', symbol: '€' },
  USD: { style: 'symbol', symbol: '$' },
  GBP: { style: 'symbol', symbol: '£' },
  CNY: { style: 'symbol', symbol: '¥' },
  CHF: { style: 'code' },
  SEK: { style: 'code' },
  PLN: { style: 'code' },
  NOK: { style: 'code' },
  DKK: { style: 'code' },
  AED: { style: 'code' },
  CAD: { style: 'symbol-and-code', symbol: '$' },
  AUD: { style: 'symbol-and-code', symbol: '$' },
  HKD: { style: 'symbol-and-code', symbol: '$' },
  SGD: { style: 'symbol-and-code', symbol: '$' },
  KRW: { style: 'symbol-and-code', symbol: '₩' },
}

export type FormatCurrencyOptions = {
  /** Defaults to 0. */
  minimumFractionDigits?: number
  /** Defaults to 2. */
  maximumFractionDigits?: number
  /** Thousands separator. Defaults to false. */
  useGrouping?: boolean
}

const formatters = new Map<string, Intl.NumberFormat>()

const numberFormat = (
  minimumFractionDigits: number,
  maximumFractionDigits: number,
  useGrouping: boolean,
): Intl.NumberFormat => {
  const key = `${minimumFractionDigits}|${maximumFractionDigits}|${useGrouping}`
  let formatter = formatters.get(key)
  if (!formatter) {
    formatter = new Intl.NumberFormat('en-US', {
      minimumFractionDigits,
      maximumFractionDigits,
      useGrouping,
    })
    formatters.set(key, formatter)
  }
  return formatter
}

const normalizeCode = (currencyCode: string): string =>
  currencyCode.trim().toUpperCase()

const compose = (
  currencyCode: string,
  amount: string,
  negative = false,
): string => {
  const code = normalizeCode(currencyCode)
  const sign = negative ? '-' : ''
  if (!code) return `${sign}${amount}`
  const display = CURRENCY_DISPLAY[code] ?? { style: 'code' }
  switch (display.style) {
    case 'symbol':
      return `${sign}${display.symbol}${amount}`
    case 'code':
      return `${sign}${amount} ${code}`
    case 'symbol-and-code':
      return `${sign}${display.symbol}${amount} ${code}`
  }
}

/**
 * Formats an amount in its currency's customer-facing display:
 * `€99`, `99 CHF`, `$99 CAD`. Never a locale-derived prefix like `CA$`.
 * Non-finite amounts render as zero.
 */
export const formatCurrency = (
  amount: number,
  currencyCode: string,
  {
    minimumFractionDigits = 0,
    maximumFractionDigits = 2,
    useGrouping = false,
  }: FormatCurrencyOptions = {},
): string => {
  const value = Number.isFinite(amount) ? amount : 0
  const digits = numberFormat(
    minimumFractionDigits,
    maximumFractionDigits,
    useGrouping,
  ).format(Math.abs(value))
  const negative = value < 0 && Number.parseFloat(digits) !== 0
  return compose(currencyCode, digits, negative)
}

/**
 * The display as a template with a placeholder for the amount:
 * `€[price]`, `[price] CHF`, `$[price] CAD`. For consumers that format the
 * number themselves, such as the measurements web components.
 */
export const currencyTemplate = (
  currencyCode: string,
  placeholder = '[price]',
): string => compose(currencyCode, placeholder)

/** The symbol shown for a currency, or its code when it has none. */
export const currencySymbol = (currencyCode: string): string => {
  const code = normalizeCode(currencyCode)
  const display = CURRENCY_DISPLAY[code]
  return display && display.style !== 'code' ? display.symbol : code
}
